import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ChartDateIntervalSelectorMapperService } from '@core/components/charts/chart-date-interval-selector/services/chart-date-interval-selector.mapper.service';
import { STATUS_NAMES_DONE, STATUS_NAMES_EXCEPT_CANCELED, STATUS_NAMES_EXCEPT_CANCELED_AND_DONE } from '@core/consts';
import { VolumetryLastOpenedAtSubject } from '@core/interfaces/reports/volumetry';
import { SidenavService } from '@core/services/sidenav.service';
import { endOfDay, mapId, mapIds, startOfDay } from '@core/utils/filters-utils';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { isEqual, sumBy } from 'lodash';
import moment from 'moment';
import { merge, Subject } from 'rxjs';
import { concatMap, debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { LineChartData } from '../../../core/components/charts/line/line.component';
import { delay } from '../../../core/utils/delay';
import { VolumetryService } from './api/queries.service';
import { FilterComponent } from './filter/filter.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'volumetry',
  templateUrl: './volumetry.component.html',
  styleUrls: ['./volumetry.component.scss'],
  animations: [fuseAnimations],
})
export class VolumetryComponent implements OnInit, OnDestroy {

  @ViewChild(FilterComponent, { static: true })
  public filter: FilterComponent;
  public isFilterOn: Boolean;
  public isLoading = true;
  public averageByTimelineGraphTotalValue: number;
  public averageCompletedByApplicationTotalValue: number;
  public isLoadingOnlyLineChart = true;
  private intervalId: any;
  private subscriptions = new Subject<void>();
  private reloadEvent = new Subject<void>();
  private autoReloadTime = 5000;
  public lastOpenedAtResponse: string;
  public hasData: Boolean;
  public lineChartData: any;

  public charts = {
    types: {
      items: [],
    },
    segments: {
      items: [],
    },
    topPendingTime: {
      items: [],
    },
    pendingTimePerInterval: {
      items: [],
      total: 0
    },
    maintenanceCompletedByApplication: {
      items: []
    },
  };


  public maintenanceCompletedTimeline: LineChartData[] = [];

  constructor(
    private readonly chartDateIntervalSelectorMapperService: ChartDateIntervalSelectorMapperService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private fuseProgressBarService: FuseProgressBarService,
    private volumetryService: VolumetryService,
    private sidenavService: SidenavService,
    private router: Router,
    private location: Location
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.autoRefresh({ checked: false });

    merge(
      this.filter.filterForm.valueChanges,
      this.reloadEvent
    ).pipe(
      takeUntil(this.subscriptions),
      filter(() => this.filter.filterForm.valid),
      tap(() => this.isLoading = true),
      debounceTime(500),
    )
      .pipe(switchMap(() => this.updateData()))
      .pipe(tap(() => {
        this.isLoading = false;
        this.isLoadingOnlyLineChart = false;
      }))
      .subscribe(() => {
        this.isFilterOn = !isEqual(this.filter.filterForm.value, this.filter.defaultFilterValue);
        this.checkIfThereIsData();
      });

    this.reload();
    this.checkIfThereIsData();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  public getDateLineChartDateInterval(dateInterval: 'DAY' | 'WEEK' | 'MONTH'): void {
    this.lastOpenedAtResponse = dateInterval;

    if (this.filter.filterForm.invalid || !this.lineChartData) {
      return;
    }

    this.updateLineChartData();
  }

  private async updateData(): Promise<void> {
    try {
      this.fuseProgressBarService.show();
      const where = this.toQuery(this.filter.filterForm.value);
      const [
        accountMaintenancesSegments,
        accountMaintenancesTypes,
        accountMaintenancesPendingTimeInterval,
        accountMaintenancesTopPendingTime,
        accountMaintenancesCompletedBySources,
        accountMaintenancesCompletedTimeline
      ] = await Promise.all([
        this.volumetryService.accountMaintenancesSegments(where),
        this.volumetryService.accountMaintenancesTypes(where),
        this.volumetryService.accountMaintenancesPendingTimeInterval(where),
        this.volumetryService.accountMaintenancesTopPendingTime(where),
        this.volumetryService.accountMaintenancesCompletedBySources(where),
        this.volumetryService.accountMaintenancesCompletedLineChart(where),
        delay(700)
      ]);
      this.charts.segments.items = accountMaintenancesSegments;
      this.charts.pendingTimePerInterval.items = accountMaintenancesPendingTimeInterval;
      this.charts.types.items = accountMaintenancesTypes;
      this.charts.pendingTimePerInterval.total = sumBy(accountMaintenancesPendingTimeInterval, 'value');
      this.charts.topPendingTime.items = accountMaintenancesTopPendingTime;
      this.charts.maintenanceCompletedByApplication.items = accountMaintenancesCompletedBySources;

      this.lineChartData = accountMaintenancesCompletedTimeline;
      const maintenanceCompletedTimeline = [this.chartDateIntervalSelectorMapperService.toLineChartFormat(
        accountMaintenancesCompletedTimeline,
        this.lastOpenedAtResponse
      )];
      this.maintenanceCompletedTimeline = this.mapLineChartData(maintenanceCompletedTimeline);

    } catch (err) {
      console.error(err);
      sentry.captureException(err);
    } finally {
      this.fuseProgressBarService.hide();
    }
  }

  private toQuery(value: any): any {
    return {
      locationGroupId_in: mapIds(value.locationGroups),
      locationId_in: mapIds(value.locations),
      typeId_in: mapIds(value.maintenanceTypes),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      from: startOfDay(value.openedAtGte),
      to: endOfDay(value.openedAtLte),
      scheduledAt_gte: startOfDay(value.scheduledAtGte, 'YYYY-MM-DD'),
      scheduledAt_lte: endOfDay(value.scheduledAtLte, 'YYYY-MM-DD'),
      closedAt_gte: startOfDay(value.closedAtGte),
      closedAt_lte: endOfDay(value.closedAtLte),
    };
  }

  autoRefresh(event: { checked: boolean }): void {
    if (event.checked) {
      this.intervalId = setInterval(() => this.reloadEvent.next(), this.autoReloadTime);
    } else {
      clearInterval(this.intervalId);
    }
  }

  public async updateLineChartData(): Promise<any> {
    try {
      this.isLoadingOnlyLineChart = true;
      await delay(300);
      const maintenanceCompletedTimeline = [this.chartDateIntervalSelectorMapperService.toLineChartFormat(
        this.lineChartData,
        this.lastOpenedAtResponse
      )];
      this.maintenanceCompletedTimeline = this.mapLineChartData(maintenanceCompletedTimeline);
      this.checkIfThereIsData();
    } catch (err) {
      sentry.captureException(err);
    } finally {
      this.isLoadingOnlyLineChart = false;
    }
  }

  private checkIfThereIsData(): void {
    this.maintenanceCompletedTimeline.forEach((item: any) => {
      if (item.series && item.series.length) {
        this.hasData = item.series.some((serie: any) => serie.value !== 0);
      }
    });
  }

  public openSidenav(): void {
    this.sidenavService.open('filterDashboard');
  }

  public reload(): void {
    this.reloadEvent.next();
  }

  public clearFilters(): void {
    this.filter.filterForm.patchValue(this.filter.defaultFilterValue);
  }

  public yAxisFormatToInteger(val: any): string {
    if (val % 1 > 0) {
      return '';
    }
    return val;
  }

  public onSelectSegment(event: any): void {
    const queryParams = this.toMaintenancePageQueryParams();
    queryParams.segmentId_in = event.extra.segment.id;
    queryParams.status_in = STATUS_NAMES_EXCEPT_CANCELED.join();
    const urlTree = this.router.createUrlTree(['manutencoes'], { queryParams });
    const url = this.router.serializeUrl(urlTree);

    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  public onSelectMaintenanceType(event: any): void {
    const queryParams = this.toMaintenancePageQueryParams();
    queryParams.typeId_in = event.extra.maintenanceType.id;
    queryParams.status_in = STATUS_NAMES_EXCEPT_CANCELED.join();
    const urlTree = this.router.createUrlTree(['manutencoes'], { queryParams });
    const url = this.router.serializeUrl(urlTree);

    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  public onSelectPendingTimePerInterval(event: any): void {
    const queryParams = this.toMaintenancePageQueryParams();
    queryParams.status_in = STATUS_NAMES_EXCEPT_CANCELED_AND_DONE.join();
    queryParams.openedAt_gte = moment(event.extra.openedAtMin).format();
    queryParams.openedAt_lte = moment(event.extra.openedAtMax).format();
    const urlTree = this.router.createUrlTree(['manutencoes'], { queryParams });
    const url = this.router.serializeUrl(urlTree);

    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  public onSelectTopPendingPerInterval(event: any): void {
    const queryParams = this.toMaintenancePageQueryParams();
    queryParams.number_contains = event.extra.maintenance.number;
    const urlTree = this.router.createUrlTree(['manutencoes'], { queryParams });
    const url = this.router.serializeUrl(urlTree);

    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  public onSelectMaintenanceCompletedTimeline(event: any): void {
    const queryParams = this.toQuery(this.filter.filterForm.value);
    queryParams.openedAt_gte = queryParams.from;
    queryParams.openedAt_lte = queryParams.to;
    delete queryParams.from;
    delete queryParams.to;
    queryParams.status_in = STATUS_NAMES_DONE.join();
    queryParams.closedAt_gte = moment(event.initialDate).startOf('day').format();
    queryParams.closedAt_lte = moment(event.finalDate).endOf('day').format();

    const urlTree = this.router.createUrlTree(['manutencoes'], { queryParams });
    const url = this.router.serializeUrl(urlTree);

    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  private mapLineChartData(data: any): LineChartData[] {
    if (!data) { return []; }
    return data.map((item) => {
      return {
        ...item,
        series: item.series.map(({ totalCount, ...seriesItem }) => {
          return {
            ...seriesItem,
            value: totalCount
          };
        })
      };
    });
  }

  private toMaintenancePageQueryParams(): any {
    const { from: openedAt_gte, to: openedAt_lte, locationGroupId_in: locationsGroupsId_in, ...rest } = this.toQuery(this.filter.filterForm.value);
    return {
      ...rest,
      openedAt_gte,
      openedAt_lte,
      locationsGroupsId_in
    };
  }
}
